"use client";

import { notFound } from "next/navigation";
import Link from "next/link";
import { ArrowLeft, CheckCircle, Zap, BarChart, Calendar, Code2, Trophy, Star } from "lucide-react";
import { getContentBySlug } from "@/lib/content-engine"; 
import { UniversalLogo } from "@/components/ui/UniversalLogo"; 
import { use } from "react";

export default function WorkDetails({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params);
  const project = getContentBySlug(slug);

  if (!project || project.type !== 'case-study') {
    notFound();
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      
      {/* --- HERO SECTION --- */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 pt-8 pb-12">
          
          <Link href="/case-studies" className="inline-flex items-center text-slate-500 hover:text-red-600 mb-6 transition-colors text-sm font-medium">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Case Studies
          </Link>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            
            {/* LEFT COLUMN: TITLE & INTRO (8 Cols) */}
            <div className="lg:col-span-8">
              
              <div className="flex items-center gap-4 mb-4">
                 <div className="h-12 w-auto flex items-center">
                    <UniversalLogo 
                      src={project.logo} 
                      alt={project.clientName || "Client"} 
                      fallbackText={project.clientName}
                      className="h-full w-auto object-contain object-left" 
                    />
                 </div>
                 <div className="h-6 w-px bg-slate-200"></div>
                 <span className="text-slate-500 text-xs font-bold uppercase tracking-wider">
                  {project.industry || project.category}
                 </span>
              </div>
              
              <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
                {project.title}
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
                {project.excerpt}
              </p>

              {/* High-Level Metrics */}
              {project.results && (
                <div className="flex flex-wrap gap-8 border-t border-slate-100 pt-6 mt-6">
                  {project.results.map((res, i) => (
                    <div key={i}>
                      <div className="text-3xl font-bold text-red-600">{res.metric}</div>
                      <div className="text-xs text-slate-500 uppercase font-bold tracking-wider mt-1">{res.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: SNAPSHOT (4 Cols - Compact in Hero) */}
            <div className="lg:col-span-4 flex justify-start lg:justify-end">
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-lg w-full lg:w-80">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2 pb-3 border-b border-slate-100">
                      <BarChart className="h-4 w-4 text-red-600" /> Project Snapshot
                    </h3>
                    
                    <div className="space-y-4">
                        <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Client</div>
                            <div className="font-medium text-slate-900 text-sm">{project.clientName}</div>
                        </div>
                        
                        <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Timeline</div>
                            <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
                              <Calendar className="h-3 w-3 text-slate-400" /> {project.date}
                            </div>
                        </div>

                        <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Tech Stack</div>
                            <div className="flex flex-wrap gap-1.5">
                            {project.techStack?.map((tech) => (
                                <span key={tech} className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] font-semibold text-slate-600">
                                  {tech}
                                </span>
                            ))}
                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-100 mt-2">
                            <Link href="/contact-us" className="block w-full py-2.5 bg-red-600 text-white text-center font-bold text-sm rounded-lg hover:bg-red-700 transition-all shadow-sm">
                                Get Similar Results
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

          </div>
        </div>
      </div>

      {/* --- AT A GLANCE (Restored to Dark Bar Strip) --- */}
      {project.atAGlance && (
        <div className="bg-slate-900 text-white py-12 border-b border-slate-800">
            <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <div className="flex items-center gap-2 mb-3 text-red-400">
                      <Zap className="h-4 w-4" />
                      <h3 className="font-bold text-base text-white uppercase tracking-wider">The Challenge</h3>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{project.atAGlance.challenge}</p>
                </div>
                <div className="md:border-l md:border-slate-700 md:pl-8">
                  <div className="flex items-center gap-2 mb-3 text-blue-400">
                      <Code2 className="h-4 w-4" />
                      <h3 className="font-bold text-base text-white uppercase tracking-wider">The Solution</h3>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{project.atAGlance.solution}</p>
                </div>
                <div className="md:border-l md:border-slate-700 md:pl-8">
                  <div className="flex items-center gap-2 mb-3 text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      <h3 className="font-bold text-base text-white uppercase tracking-wider">The Outcome</h3>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{project.atAGlance.outcome}</p>
                </div>
            </div>
            </div>
        </div>
      )}

      {/* --- MAIN CONTENT LAYOUT --- */}
      <div className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-12 gap-12 items-start">
        
        {/* LEFT: NARRATIVE (8 Cols) */}
        <div className="lg:col-span-8 space-y-16">
          
          {/* About Client */}
          {project.aboutClient && (
            <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">About {project.clientName}</h2>
                <p className="text-lg text-slate-600 leading-relaxed">{project.aboutClient}</p>
            </section>
          )}

          {/* The Challenge */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">The Challenge</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              {project.challenge}
            </p>
            {project.challengeBullets && (
                <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                    <ul className="space-y-3">
                        {project.challengeBullets.map((bullet, i) => (
                            <li key={i} className="flex items-start gap-3 text-slate-800 text-base">
                                <div className="h-1.5 w-1.5 rounded-full bg-red-600 mt-2.5 shrink-0" />
                                <span>{bullet}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
          </section>

          {/* The Solution */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">The Solution</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              {project.solution}
            </p>
             {project.solutionBullets && (
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-500 mb-4 text-xs uppercase tracking-wider">
                        Implementation Details
                    </h3>
                    <ul className="grid gap-4">
                    {project.solutionBullets.map((bullet, i) => {
                        // Highlight "Guardrails" for AWS Certification evidence
                        const isGuardrail = bullet.toLowerCase().includes("guardrail") || bullet.toLowerCase().includes("security");
                        return (
                          <li key={i} className={`flex gap-3 p-3 rounded-lg ${isGuardrail ? "bg-blue-50 border border-blue-100" : ""}`}>
                              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold mt-1 ${isGuardrail ? "bg-blue-600 text-white" : "bg-slate-900 text-white"}`}>
                                  {i + 1}
                              </div>
                              <p className={`text-base leading-relaxed ${isGuardrail ? "text-slate-900 font-medium" : "text-slate-700"}`}>
                                {bullet}
                              </p>
                          </li>
                        );
                    })}
                    </ul>
                </div>
            )}
          </section>

          {/* RESULTS & METRICS GRAPHIC */}
          {project.resultsImage && (
             <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Results & Metrics</h2>
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                   <img src={project.resultsImage} alt="Results & Metrics Graphic" className="w-full h-auto" />
                </div>
             </section>
          )}

          {/* IMPACT SUMMARY */}
          {project.impactSummary && (
            <section className="bg-slate-900 text-white p-8 rounded-2xl">
               <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                 <Trophy className="h-6 w-6 text-yellow-400" /> Impact Summary
               </h3>
               <ul className="space-y-4">
                 {project.impactSummary.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                        <span className="text-slate-300 leading-relaxed">{item}</span>
                    </li>
                 ))}
               </ul>
            </section>
          )}

        </div>

        {/* RIGHT: SIDEBAR (4 Cols) */}
        <div className="lg:col-span-4 space-y-8 sticky top-24">
            
            {/* WHY ALLCODE CARD (Moved to Sidebar) */}
            {project.whyAllCode && (
              <div className="bg-blue-600 text-white p-6 rounded-xl shadow-xl relative overflow-hidden w-full lg:w-80 ml-auto">
                 <div className="absolute top-0 right-0 -mr-8 -mt-8 opacity-20">
                    <Star width="120" height="120" fill="currentColor" />
                 </div>
                 <h3 className="font-bold text-base mb-3 relative z-10 uppercase tracking-wider opacity-90">Why AllCode?</h3>
                 <p className="text-white leading-relaxed text-sm relative z-10 font-medium">
                   {project.whyAllCode}
                 </p>
              </div>
            )}

            {/* TESTIMONIAL (If exists) */}
            {project.testimonial && (
               <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm w-full lg:w-80 ml-auto">
                  <div className="text-slate-300 text-4xl font-serif mb-2">"</div>
                  <p className="text-slate-700 italic text-sm mb-4">
                    {project.testimonial.quote}
                  </p>
                  <div className="text-xs font-bold text-slate-900">
                    {project.testimonial.author}, <span className="text-slate-500 font-normal">{project.testimonial.role}</span>
                  </div>
               </div>
            )}
        </div>

      </div>
    </div>
  );
}